// src/components/artwork/tabs/ArtworkLocationTab.jsx
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { MapPin, Building2, Home, Warehouse, Globe, AlertCircle, Clock, AlertTriangle, X, Check } from "lucide-react";
import { supabase } from "../../../lib/supabaseClient";
import { useTranslation } from "react-i18next";
import ConservationDiagnostic from "../ConservationDiagnostic";
import ArtworkPlacement from "../ArtworkPlacement";

const LOSS_TYPES = [
  { value: "vol", color: "#991b1b" },
  { value: "perte", color: "#92400e" },
  { value: "destruction", color: "#7c3aed" },
  { value: "incendie", color: "#c2410c" },
  { value: "inondation", color: "#1e40af" },
  { value: "autre", color: "#374151" },
];

function LossDeclarationModal({ formData, userId, onClose, onSaved }) {
  const { t } = useTranslation();
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({
    loss_type: "vol",
    loss_date: today,
    declared_value: formData?.value || formData?.estimated_value || "",
    police_report: false,
    police_ref: "",
    insurance_claim: false,
    insurance_ref: "",
    indemnity: "",
    location_at_loss: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!formData?.id || !userId) return;
    setSaving(true);
    setError(null);
    try {
      const { error: err } = await supabase.from("artwork_losses").insert({
        client_id:        formData.client_id || userId,
        artwork_id:       formData.id,
        artwork_title:    formData.title || "",
        artist:           formData.artist || "",
        inventory_number: formData.inventory_number || "",
        loss_type:        form.loss_type,
        loss_date:        form.loss_date,
        tax_year:         new Date(form.loss_date).getFullYear(),
        declared_value:   form.declared_value ? Number(form.declared_value) : null,
        purchase_price:   formData.acquisition_price || null,
        currency:         formData.currency || "EUR",
        police_report:    form.police_report,
        police_ref:       form.police_ref || null,
        insurance_claim:  form.insurance_claim,
        insurance_ref:    form.insurance_ref || null,
        indemnity:        form.indemnity ? Number(form.indemnity) : null,
        location_at_loss: form.location_at_loss || null,
        notes:            form.notes || null,
        created_by:       userId,
      });
      if (err) throw err;
      setSaved(true);
      setTimeout(() => { onSaved?.(); onClose(); }, 1500);
    } catch (e) {
      setError(e.message || t("artworkLocationTab.lossModal.error"));
    } finally {
      setSaving(false);
    }
  };

  const lossType = LOSS_TYPES.find((t) => t.value === form.loss_type);
  const inp = { border: "1px solid #e8e3db", background: "#fff", padding: "8px 12px", fontFamily: "Georgia, serif", fontSize: "14px", color: "#1a1a1a", outline: "none", boxSizing: "border-box", width: "100%" };
  const lbl = { fontFamily: "Georgia, serif", fontSize: "11px", letterSpacing: "0.08em", textTransform: "uppercase", color: "#888", display: "block", marginBottom: "6px" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
      <div style={{ background: "#fdfcfa", border: "1px solid #e8e3db", width: "100%", maxWidth: "560px", maxHeight: "90vh", overflowY: "auto", padding: "32px", position: "relative" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px" }}>
          <div>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", letterSpacing: "0.12em", textTransform: "uppercase", color: "#c9a961", marginBottom: "6px" }}>{t("artworkLocationTab.lossModal.badge")}</p>
            <h2 style={{ fontFamily: "Georgia, serif", fontSize: "22px", fontWeight: "normal", color: "#1a1a1a", margin: 0 }}>{t("artworkLocationTab.lossModal.title")}</h2>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#888", marginTop: "4px", fontStyle: "italic" }}>
              {formData?.title || t("artworkLocationTab.lossModal.untitled")}{formData?.artist ? ` — ${formData.artist}` : ""}
            </p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#888", padding: "4px" }}><X size={20} /></button>
        </div>

        {saved ? (
          <div style={{ textAlign: "center", padding: "32px 0" }}>
            <Check size={40} strokeWidth={1} style={{ color: "#c9a961", margin: "0 auto 12px", display: "block" }} />
            <p style={{ fontFamily: "Georgia, serif", fontSize: "16px", color: "#1a1a1a" }}>{t("artworkLocationTab.lossModal.successTitle")}</p>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#888", fontStyle: "italic" }}>{t("artworkLocationTab.lossModal.successBody")}</p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: "20px" }}>
              <span style={lbl}>{t("artworkLocationTab.lossModal.eventType")}</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {LOSS_TYPES.map((lt) => (
                  <button key={lt.value} onClick={() => set("loss_type", lt.value)} style={{ padding: "6px 16px", border: `1px solid ${form.loss_type === lt.value ? lt.color : "#e8e3db"}`, background: form.loss_type === lt.value ? lt.color : "transparent", color: form.loss_type === lt.value ? "#fff" : "#555", fontFamily: "Georgia, serif", fontSize: "13px", cursor: "pointer" }}>
                    {t(`artworkLocationTab.lossTypes.${lt.value}`)}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "20px" }}>
              <div>
                <span style={lbl}>{t("artworkLocationTab.lossModal.eventDate")}</span>
                <input type="date" value={form.loss_date} onChange={(e) => set("loss_date", e.target.value)} style={inp} />
              </div>
              <div>
                <span style={lbl}>{t("artworkLocationTab.lossModal.declaredValue")}</span>
                <input type="number" value={form.declared_value} onChange={(e) => set("declared_value", e.target.value)} placeholder={t("artworkLocationTab.lossModal.estimatedValue")} style={inp} />
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <span style={lbl}>{t("artworkLocationTab.lossModal.location")}</span>
              <input type="text" value={form.location_at_loss} onChange={(e) => set("location_at_loss", e.target.value)} placeholder={t("artworkLocationTab.lossModal.locationPlaceholder")} style={inp} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "20px" }}>
              <div style={{ border: "1px solid #e8e3db", padding: "14px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", marginBottom: form.police_report ? "10px" : 0 }}>
                  <input type="checkbox" checked={form.police_report} onChange={(e) => set("police_report", e.target.checked)} />
                  <span style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#1a1a1a" }}>{t("artworkLocationTab.lossModal.policeReport")}</span>
                </label>
                {form.police_report && <input type="text" value={form.police_ref} onChange={(e) => set("police_ref", e.target.value)} placeholder={t("artworkLocationTab.lossModal.policeRef")} style={{ ...inp, marginTop: "8px" }} />}
              </div>
              <div style={{ border: "1px solid #e8e3db", padding: "14px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", marginBottom: form.insurance_claim ? "10px" : 0 }}>
                  <input type="checkbox" checked={form.insurance_claim} onChange={(e) => set("insurance_claim", e.target.checked)} />
                  <span style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#1a1a1a" }}>{t("artworkLocationTab.lossModal.insuranceClaim")}</span>
                </label>
                {form.insurance_claim && (
                  <>
                    <input type="text" value={form.insurance_ref} onChange={(e) => set("insurance_ref", e.target.value)} placeholder={t("artworkLocationTab.lossModal.insuranceRef")} style={{ ...inp, marginBottom: "6px" }} />
                    <input type="number" value={form.indemnity} onChange={(e) => set("indemnity", e.target.value)} placeholder={t("artworkLocationTab.lossModal.indemnity")} style={inp} />
                  </>
                )}
              </div>
            </div>

            <div style={{ marginBottom: "24px" }}>
              <span style={lbl}>{t("artworkLocationTab.lossModal.notes")}</span>
              <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder={t("artworkLocationTab.lossModal.notesPlaceholder")} rows={3} style={{ ...inp, resize: "vertical" }} />
            </div>

            <div style={{ background: "#fffbf2", border: "1px solid #c9a961", padding: "12px 16px", marginBottom: "24px" }}>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#7a6030", margin: 0, lineHeight: 1.6 }}>
                {t("artworkLocationTab.lossModal.fiscalNote")}
              </p>
            </div>

            {error && (
              <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", padding: "10px 14px", marginBottom: "16px" }}>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#991b1b", margin: 0 }}>{error}</p>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <button onClick={onClose} style={{ background: "none", border: "1px solid #e8e3db", padding: "10px 20px", fontFamily: "Georgia, serif", fontSize: "13px", color: "#888", cursor: "pointer" }}>{t("artworkLocationTab.lossModal.cancel")}</button>
              <button onClick={handleSubmit} disabled={saving} style={{ background: lossType?.color || "#1a1a1a", border: "none", color: "#fff", padding: "10px 24px", fontFamily: "Georgia, serif", fontSize: "13px", letterSpacing: "0.06em", cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}>
                {saving ? t("artworkLocationTab.lossModal.saving") : t("artworkLocationTab.lossModal.save")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Icône selon le type d'emplacement
const LocationTypeIcon = ({ type, size = 16 }) => {
  const props = { size, strokeWidth: 1.5 };
  switch ((type || "").toLowerCase()) {
    case "musée":
    case "musee":
    case "museum":
    case "galerie":
    case "gallery":
      return <Building2 {...props} />;
    case "domicile":
    case "home":
    case "résidence":
    case "residence":
      return <Home {...props} />;
    case "entrepôt":
    case "entrepot":
    case "warehouse":
    case "stockage":
    case "storage":
      return <Warehouse {...props} />;
    case "prêt":
    case "pret":
    case "loan":
    case "exposition":
    case "exhibition":
      return <Globe {...props} />;
    default:
      return <MapPin {...props} />;
  }
};


const locationTypeKey = (type) => {
  const value = (type || "").toLowerCase();
  if (["musée", "musee", "museum"].includes(value)) return "museum";
  if (["galerie", "gallery"].includes(value)) return "gallery";
  if (["domicile", "home"].includes(value)) return "home";
  if (["résidence", "residence"].includes(value)) return "residence";
  if (["entrepôt", "entrepot", "warehouse"].includes(value)) return "warehouse";
  if (["stockage", "storage"].includes(value)) return "storage";
  if (["prêt", "pret", "loan"].includes(value)) return "loan";
  if (["exposition", "exhibition"].includes(value)) return "exhibition";
  if (["autre", "other"].includes(value)) return "other";
  return null;
};

// Badge type d'emplacement
const LocationBadge = ({ type }) => {
  const { t } = useTranslation();
  if (!type) return null;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        fontSize: "11px",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        fontFamily: "Georgia, serif",
        color: "#c9a961",
        border: "1px solid #c9a961",
        padding: "2px 10px",
      }}
    >
      <LocationTypeIcon type={type} size={11} />
      {locationTypeKey(type) ? t(`artworkLocationTab.locationTypes.${locationTypeKey(type)}`) : type}
    </span>
  );
};

const getLossTypeMeta = (lossType) =>
  LOSS_TYPES.find((item) => item.value === lossType) ||
  LOSS_TYPES.find((item) => item.value === "autre");

const LossStatusBadge = ({ loss }) => {
  const { t, i18n } = useTranslation();
  if (!loss) return null;

  const meta = getLossTypeMeta(loss.loss_type);
  const label = meta?.value ? t(`artworkLocationTab.lossTypes.${meta.value}`) : t("artworkLocationTab.lossStatus.incident");
  const dateLabel = loss.loss_date
    ? new Date(loss.loss_date).toLocaleDateString(i18n.language)
    : null;

  return (
    <div
      style={{
        border: `1px solid ${meta?.color || "#991b1b"}`,
        background: "#fef2f2",
        color: meta?.color || "#991b1b",
        padding: "12px 16px",
        marginBottom: "18px",
        fontFamily: "Georgia, serif",
        fontSize: "13px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        flexWrap: "wrap",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
        <AlertTriangle size={15} />
        <strong>{t("artworkLocationTab.lossStatus.declared", { type: label })}</strong>
        {dateLabel ? <span>· {dateLabel}</span> : null}
      </span>

      {loss.police_report && (
        <span style={{ fontSize: "12px", color: "#7f1d1d" }}>
          {t("artworkLocationTab.lossStatus.police")}{loss.police_ref ? ` · ${loss.police_ref}` : ""}
        </span>
      )}
    </div>
  );
};


export default function ArtworkLocationTab({
  formData,
  setFormData,
  onChange,
  readOnly = false,
  onChangeLocation,
  locations = [],
}) {
  const { t, i18n } = useTranslation();
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationHistory, setLocationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLossModal, setShowLossModal] = useState(false);
  const [latestLoss, setLatestLoss] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUserId(data?.session?.user?.id || null);
    });
  }, []);

  const fetchLocationData = useCallback(async () => {
    if (!formData?.id) {
      setCurrentLocation(null);
      setLocationHistory([]);
      setLatestLoss(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Emplacement actuel
      if (formData?.location_id) {
        const { data: loc } = await supabase
          .from("locations")
          .select("*")
          .eq("id", formData.location_id)
          .maybeSingle();

        setCurrentLocation(loc || null);
      } else {
        setCurrentLocation(null);
      }

      // Dernière déclaration active de perte / vol / destruction.
      // recovered_at NULL = l'œuvre est encore considérée comme non retrouvée.
      const { data: loss, error: lossError } = await supabase
        .from("artwork_losses")
        .select("*")
        .eq("artwork_id", formData.id)
        .is("recovered_at", null)
        .order("loss_date", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (lossError) {
        console.error("Erreur chargement perte/vol:", lossError);
      }

      setLatestLoss(loss || null);

      // Historique depuis la provenance (champs avec location renseigné)
      if (formData?.provenance && Array.isArray(formData.provenance)) {
        const history = formData.provenance
          .filter((item) => item.location)
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        setLocationHistory(history);
      } else {
        setLocationHistory([]);
      }
    } catch (err) {
      console.error("Erreur chargement emplacement:", err);
    } finally {
      setLoading(false);
    }
  }, [formData?.id, formData?.location_id, formData?.provenance]);

  useEffect(() => {
    fetchLocationData();
  }, [fetchLocationData]);

  const handleLossSaved = () => {
    setShowLossModal(false);
    fetchLocationData();
  };

  // ─── LOADING ───────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: "48px 0", textAlign: "center" }}>
        <div
          style={{
            width: "32px",
            height: "32px",
            border: "1px solid #1a1a1a",
            borderTopColor: "transparent",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
            margin: "0 auto",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ─── STYLES PARTAGÉS ───────────────────────────────────────────
  const sectionTitle = {
    fontFamily: "Georgia, serif",
    fontSize: "11px",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#888",
    marginBottom: "16px",
    display: "block",
  };

  const card = {
    border: "1px solid #e8e3db",
    background: "#fdfcfa",
    padding: "24px",
  };

  const labelStyle = {
    fontFamily: "Georgia, serif",
    fontSize: "11px",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#888",
    display: "block",
    marginBottom: "4px",
  };

  const valueStyle = {
    fontFamily: "Georgia, serif",
    fontSize: "15px",
    color: "#1a1a1a",
  };

  const divider = {
    borderTop: "1px solid #e8e3db",
    margin: "24px 0",
  };

  // ─── BANNIÈRE PRÊT SORTANT ────────────────────────────────────
  const isOnLoan = formData?.availability === "on_loan" || formData?.loan_status === "on_loan";

  const LoanBanner = () => {
    if (!isOnLoan) return null;
    const fmtD = (d) => d ? new Date(d).toLocaleDateString(i18n.language) : null;
    return (
      <div style={{
        border: "1px solid #3b82f6", background: "#eff6ff",
        padding: "14px 18px", marginBottom: "18px",
        display: "flex", gap: "12px", alignItems: "flex-start",
      }}>
        <Globe size={18} strokeWidth={1.5} style={{ color: "#2563eb", flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: 700, color: "#1e40af", marginBottom: 4 }}>
            ↗ {t("artworkLocationTab.loan.outgoing")}
          </div>
          {formData?.loan_institution && (
            <div style={{ fontFamily: "Georgia, serif", fontSize: "14px", color: "#1a1a1a", marginBottom: 2 }}>
              {formData.loan_institution}
            </div>
          )}
          {formData?.storage_location_detail && !formData?.loan_institution && (
            <div style={{ fontFamily: "Georgia, serif", fontSize: "14px", color: "#1a1a1a", marginBottom: 2 }}>
              {formData.storage_location_detail}
            </div>
          )}
          <div style={{ fontFamily: "system-ui", fontSize: "12px", color: "#6b7280", display: "flex", flexWrap: "wrap", gap: "12px", marginTop: 4 }}>
            {formData?.loan_contact && <span>{t("artworkLocationTab.loan.contact", { value: formData.loan_contact })}</span>}
            {fmtD(formData?.loan_start_date) && <span>{t("artworkLocationTab.loan.from", { date: fmtD(formData.loan_start_date) })}</span>}
            {fmtD(formData?.loan_end_date) && <span>{t("artworkLocationTab.loan.to", { date: fmtD(formData.loan_end_date) })}</span>}
            {formData?.loan_contract_ref && <span>{t("artworkLocationTab.loan.agreementRef", { value: formData.loan_contract_ref })}</span>}
          </div>
          {formData?.loan_conditions && (
            <div style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#6b7280", fontStyle: "italic", marginTop: 6 }}>
              {formData.loan_conditions}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── PAS D'EMPLACEMENT VISIBLE ────────────────────────────────
  // Attention : currentLocation peut être null pour 2 raisons très différentes :
  // 1) aucune localisation n'est associée à l'œuvre ;
  // 2) une localisation existe, mais l'utilisateur courant n'a pas le droit de la lire
  //    (cas expert / partage client / RLS). Dans ce second cas, on ne doit surtout pas
  //    afficher "Aucun emplacement défini", sinon l'expert risque d'en créer un doublon.
  const hasHiddenLocation = Boolean(formData?.location_id) && !currentLocation;

  if (!currentLocation) {
    const noLocationMessage = hasHiddenLocation
      ? t("artworkLocationTab.hiddenLocation")
      : t("artworkLocationTab.noLocation");

    // Si l'œuvre est en prêt, la bannière est l'emplacement — pas besoin d'en définir un autre
    if (isOnLoan) {
      return (
        <div style={{ paddingTop: "8px" }}>
          <LossStatusBadge loss={latestLoss} />
          <LoanBanner />
          <p style={{ fontFamily: "Georgia, serif", fontSize: 13, color: "#9ca3af", fontStyle: "italic", marginTop: 8 }}>
            {t("artworkLocationTab.loan.borrowerLocation")}
          </p>
        </div>
      );
    }

    return (
      <div style={{ paddingTop: "8px" }}>
        <LossStatusBadge loss={latestLoss} />
        <LoanBanner />

        {/* Placement (lieu + mode) + diagnostic — éditable par le propriétaire,
            lecture seule pour les tiers autorisés (advisor / assureur). */}
        {!hasHiddenLocation && formData?.id && (
          <>
            <ArtworkPlacement
              artworkId={formData.id}
              clientId={formData.client_id || userId}
              locations={locations}
              currentLocationId={null}
              readOnly={readOnly}
              onChanged={(newLocId) => { if (onChange) onChange({ location_id: newLocId }); }}
            />
            <ConservationDiagnostic
              artworkId={formData.id}
              clientId={formData.client_id || userId}
              mediumFamilyKey={formData.medium_family_key}
              locationId={null}
              readOnly={readOnly}
            />
          </>
        )}

        {/* Lieu masqué (vue partagée) : message dédié. */}
        {hasHiddenLocation && (
          <>
            <span style={sectionTitle}>{t("artworkLocationTab.currentLocation")}</span>
            <div
              style={{
                ...card,
                textAlign: "center",
                padding: "40px 24px",
                borderStyle: "dashed",
                background: "#fffbf2",
                borderColor: "#c9a961",
              }}
            >
              <AlertCircle size={28} strokeWidth={1} style={{ margin: "0 auto 12px", color: "#c9a961" }} />
              <p style={{ fontFamily: "Georgia, serif", fontSize: "15px", color: "#7a6030", fontStyle: "italic", lineHeight: 1.6, maxWidth: "680px", margin: "0 auto" }}>
                {noLocationMessage}
              </p>
            </div>
          </>
        )}

        {/* Lecture seule sans lieu : message simple. */}
        {readOnly && !hasHiddenLocation && (
          <p style={{ fontFamily: "Georgia, serif", fontSize: 13, color: "#9ca3af", fontStyle: "italic", marginTop: 8 }}>
            {noLocationMessage}
          </p>
        )}

        {!readOnly && !hasHiddenLocation && (
          <div style={{ marginTop: "20px", textAlign: "center" }}>
            <button
              onClick={() => setShowLossModal(true)}
              style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "none", border: "1px solid #991b1b", color: "#991b1b", padding: "7px 18px", fontFamily: "Georgia, serif", fontSize: "11px", letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer" }}
            >
              <AlertTriangle size={12} />
              {t("artworkLocationTab.declareLoss")}
            </button>
          </div>
        )}
        {showLossModal && <LossDeclarationModal formData={formData} userId={userId} onClose={() => setShowLossModal(false)} onSaved={handleLossSaved} />}
      </div>
    );
  }

  // ─── EMPLACEMENT TROUVÉ ────────────────────────────────────────
  return (
    <div style={{ paddingTop: "8px" }}>
      <LossStatusBadge loss={latestLoss} />
      <LoanBanner />

      {/* ── MVP « Lieux & conservation » : placement (lieu + mode + historique) ──
          Éditable par le propriétaire, lecture seule pour les tiers autorisés. */}
      {formData?.id && (
        <ArtworkPlacement
          artworkId={formData.id}
          clientId={formData.client_id || userId}
          locations={locations}
          currentLocationId={formData.location_id || null}
          readOnly={readOnly}
          onChanged={(newLocId) => {
            // Sync fiche : met à jour location_id → l'effet refetch la carte détail.
            if (onChange) onChange({ location_id: newLocId });
            else if (setFormData) setFormData((prev) => ({ ...(prev || {}), location_id: newLocId }));
          }}
        />
      )}

      {/* ── MVP « Lieux & conservation » : mini-diagnostic bienveillant par œuvre ── */}
      {formData?.id && (
        <ConservationDiagnostic
          artworkId={formData.id}
          clientId={formData.client_id || userId}
          mediumFamilyKey={formData.medium_family_key}
          locationId={formData.location_id || null}
          readOnly={readOnly}
        />
      )}

      {/* ── Emplacement actuel ── */}
      <span style={sectionTitle}>{t("artworkLocationTab.currentLocation")}</span>
      <div style={card}>
        {/* En-tête : nom + badge */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "20px",
          }}
        >
          <div>
            <h3
              style={{
                fontFamily: "Georgia, serif",
                fontSize: "20px",
                fontWeight: "normal",
                color: "#1a1a1a",
                margin: "0 0 6px",
              }}
            >
              {currentLocation.name}
            </h3>
            <LocationBadge type={currentLocation.type} />
          </div>
        </div>

        {/* Grille de détails */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "20px",
          }}
        >
          {currentLocation.address && (
            <div>
              <span style={labelStyle}>{t("artworkLocationTab.fields.address")}</span>
              <span style={valueStyle}>{currentLocation.address}</span>
            </div>
          )}
          {currentLocation.city && (
            <div>
              <span style={labelStyle}>{t("artworkLocationTab.fields.city")}</span>
              <span style={valueStyle}>{currentLocation.city}</span>
            </div>
          )}
          {currentLocation.country && (
            <div>
              <span style={labelStyle}>{t("artworkLocationTab.fields.country")}</span>
              <span style={valueStyle}>{currentLocation.country}</span>
            </div>
          )}
          {currentLocation.contact_name && (
            <div>
              <span style={labelStyle}>{t("artworkLocationTab.fields.contact")}</span>
              <span style={valueStyle}>{currentLocation.contact_name}</span>
            </div>
          )}
          {currentLocation.contact_email && (
            <div>
              <span style={labelStyle}>{t("artworkLocationTab.fields.email")}</span>
              <span style={{ ...valueStyle, fontSize: "14px" }}>
                <a
                  href={`mailto:${currentLocation.contact_email}`}
                  style={{ color: "#1a1a1a", textDecoration: "underline" }}
                >
                  {currentLocation.contact_email}
                </a>
              </span>
            </div>
          )}
          {currentLocation.contact_phone && (
            <div>
              <span style={labelStyle}>{t("artworkLocationTab.fields.phone")}</span>
              <span style={valueStyle}>{currentLocation.contact_phone}</span>
            </div>
          )}
        </div>

        {/* Notes */}
        {currentLocation.notes && (
          <>
            <div style={divider} />
            <div>
              <span style={labelStyle}>{t("artworkLocationTab.fields.notes")}</span>
              <p
                style={{
                  fontFamily: "Georgia, serif",
                  fontSize: "14px",
                  color: "#555",
                  lineHeight: "1.7",
                  fontStyle: "italic",
                  margin: 0,
                }}
              >
                {currentLocation.notes}
              </p>
            </div>
          </>
        )}
      </div>

      {/* ── Historique des emplacements ── */}
      {locationHistory.length > 0 && (
        <>
          <div style={{ ...divider, margin: "32px 0 20px" }} />
          <span style={sectionTitle}>{t("artworkLocationTab.history")}</span>
          <div style={{ border: "1px solid #e8e3db" }}>
            {locationHistory.map((item, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "14px 20px",
                  borderBottom:
                    index < locationHistory.length - 1
                      ? "1px solid #e8e3db"
                      : "none",
                  background: "#fdfcfa",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <Clock size={14} strokeWidth={1.5} style={{ color: "#bbb", flexShrink: 0 }} />
                  <div>
                    <p
                      style={{
                        fontFamily: "Georgia, serif",
                        fontSize: "14px",
                        color: "#1a1a1a",
                        margin: "0 0 2px",
                      }}
                    >
                      {item.description}
                    </p>
                    {item.location && (
                      <p
                        style={{
                          fontFamily: "Georgia, serif",
                          fontSize: "12px",
                          color: "#888",
                          margin: 0,
                          fontStyle: "italic",
                        }}
                      >
                        {item.location}
                      </p>
                    )}
                  </div>
                </div>
                <span
                  style={{
                    fontFamily: "Georgia, serif",
                    fontSize: "12px",
                    color: "#888",
                    whiteSpace: "nowrap",
                    marginLeft: "16px",
                  }}
                >
                  {new Date(item.date).toLocaleDateString(i18n.language, {
                    year: "numeric",
                    month: "long",
                  })}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* « Autres emplacements disponibles » fusionné dans ArtworkPlacement
          (le changement de lieu se fait désormais via son sélecteur, qui crée un
          placement daté et resynchronise artworks.location_id). */}

      {/* ── Lien vers gestion + déclaration perte/vol ── */}
      {!readOnly && (
        <>
          <div style={{ ...divider, margin: "32px 0 20px" }} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
            <Link
              to="/locations"
              style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontFamily: "Georgia, serif", fontSize: "12px", letterSpacing: "0.06em", textTransform: "uppercase", color: "#888", textDecoration: "none" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#1a1a1a")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#888")}
            >
              <Building2 size={13} />
              {t("artworkLocationTab.manage")}
            </Link>
            <button
              onClick={() => setShowLossModal(true)}
              style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "none", border: "1px solid #991b1b", color: "#991b1b", padding: "7px 18px", fontFamily: "Georgia, serif", fontSize: "11px", letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer" }}
            >
              <AlertTriangle size={12} />
              {t("artworkLocationTab.declareLoss")}
            </button>
          </div>
        </>
      )}
      {showLossModal && <LossDeclarationModal formData={formData} userId={userId} onClose={() => setShowLossModal(false)} onSaved={handleLossSaved} />}
    </div>
  );
}